
import React from 'react'
import JoiBrowser from 'joi-browser'
import { useMutation } from '@apollo/client'
import { IoMdAdd } from 'react-icons/io'
import {
  Button,
  Flex,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { toast } from 'react-hot-toast'

import FileUpload from 'components/FileUpload'
import { MemoizedInput } from 'components/MemoizedInput'

import PlatformCertificateMutation from './__mutation__/index.plateform.certificate.mutation'

const Index = ({ title }) => {
  const _isCurrentViewMobile = useBreakpointValue({
    base: true,
    sm: true,
    md: false,
    lg: false,
    xl: false,
  })

  const [error, setError] = React.useState('')
  const [file, setFile] = React.useState(null)
  const [password, setPassword] = React.useState("")
  const [uploadingFile, setUploadingFile] = React.useState(false)

  // Standard mutation pattern like your reference
  const [MutationPlatformCertificate, MutationPlatformCertificateResponse] = useMutation(PlatformCertificateMutation)

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]

    // Clear previous errors
    setError('')

    // File validation
    if (selectedFile) {
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (selectedFile.size > maxSize) {
        setError('File size should be less than 10MB')
        return
      }

      const allowedTypes = ['.p12', '.crt', '.pem', '.key', '.pfx']
      const fileExtension = '.' + selectedFile.name.split('.').pop().toLowerCase()

      if (!allowedTypes.includes(fileExtension)) {
        setError('Please select a valid certificate file')
        return
      }
    }

    setFile(selectedFile)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Simple validation using joi-browser (same as your existing pattern)
    const _JoiSchema = JoiBrowser.object({
      password: JoiBrowser.string().required(),

    }).options({ allowUnknown: true })

    const formData = {
      password,
    }

    const _JoiSchemaValidate = _JoiSchema.validate(formData)

    if (_JoiSchemaValidate.error) {
      setError(_JoiSchemaValidate.error?.message)
      return
    }

    // Additional manual validation for better UX
    if (!file) {
      setError('Please select a certificate file')
      return
    }

    if (!password || password.trim().length === 0) {
      setError('Password is required')
      return
    }

    try {
      // Set uploading state
      setUploadingFile(true)

      // Execute mutation
      const _MutationPlatformCertificateResponse = await MutationPlatformCertificate({
        variables: {
          passphrase: password,
          file: file,
        }
      })

      // Set uploading state to false
      setUploadingFile(false)

      // Get response data
      const responseData = _MutationPlatformCertificateResponse?.data?.PlatformCertificate

      // Check if mutation was successful and response exists
      if (responseData) {
        // Check status and handle accordingly
        if (responseData.status === 'SUCCESS' || responseData.status === 'success') {
          // Success toast message
          toast.success(responseData.message || 'Certificate uploaded successfully!', {
            position: 'bottom-left',
            duration: 4000,
          })

          // Reset form on success
          setFile(null)
          setPassword("")

          // Reset file input
          const fileInput = document.querySelector('input[type="file"]')
          if (fileInput) fileInput.value = ''

        } else {
          // Error from server
          const errorMsg = responseData.message || 'Upload failed'
          toast.error(errorMsg, {
            position: 'bottom-left',
            duration: 4000,
          })
          setError(errorMsg)
        }
      } else {
        // No response data received
        throw new Error('No response received from server')
      }

    } catch (err) {
      // Set uploading state to false on error
      setUploadingFile(false)

      // Handle GraphQL errors or network errors
      let errorMessage = 'Upload failed'

      if (err.graphQLErrors && err.graphQLErrors.length > 0) {
        errorMessage = err.graphQLErrors[0].message
      } else if (err.networkError) {
        errorMessage = 'Network error: Please check your connection'
      } else if (err.message) {
        errorMessage = err.message
      }

      setError(errorMessage)
      toast.error(errorMessage, {
        position: 'bottom-left',
        duration: 4000,
      })
    }
  }

  return (
    <Flex className='smppsetting base'>
      <Flex
        direction="column"
        p={_isCurrentViewMobile ? '12px' : '22px'}
        bg="white"
        height="100%"
        borderRadius='22px'
        boxShadow='14px 17px 40px 4px rgba(112, 144, 176, 0.17)'
        mx="auto"
        fontFamily='DM Sans'
        fontSize="inherit"
        fontWeight="inherit"
        width="100%"
      >
        <form style={{ width: '100%' }} onSubmit={handleSubmit}>
          <Text fontSize={17} fontWeight={500} mb={6}>
            {title}
          </Text>

          {/* File Upload */}
          <Flex gap='12px' mb={6}>
            <FileUpload
              uploadingFile={uploadingFile}
              label='Upload Certificate'
              type="file"
              accept=".p12,.crt,.pem,.key,.pfx"
              onChange={handleFileChange}
              error={error}
              isInvalid={error?.includes('file') || error?.includes('Certificate')}
              disabled={uploadingFile || MutationPlatformCertificateResponse.loading}
            />
          </Flex>

          {/* Show selected file */}
          {file && (
            <Text fontSize="sm" color="green.500" mb={2}>
              {file.name} ({(file.size / 1024).toFixed(2)} KB)
            </Text>
          )}

          {/* Password Input */}
          <Text fontSize={17} fontWeight={500} mb={6}>
            Certificate File Password
          </Text>
          <MemoizedInput
            placeholder='Enter certificate password'
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            mb={6}
            error={error}
            isInvalid={error?.includes('password') || error?.includes('Password')}
            disabled={uploadingFile || MutationPlatformCertificateResponse.loading}
          />

          {/* Submit Button */}
          <Flex mt='12px' w='100%' flexDir='column' >
            <Button
              px='12px'
              h='40px'
              w='100%'
              gap='6px'
              bg='brand.500'
              _hover={{ bg: 'brand.600' }}
              _active={{ bg: 'brand.600' }}
              color='white'
              type="submit"
              isLoading={MutationPlatformCertificateResponse.loading || uploadingFile}
              loadingText="Uploading..."
              isDisabled={!file || !password || uploadingFile || MutationPlatformCertificateResponse.loading}
            >
              <IoMdAdd style={{ marginRight: 6, fontSize: 22 }} />
              Submit
            </Button>
          </Flex>
        </form>
      </Flex>
    </Flex>
  )
}

Index.propTypes = {
  'title': PropTypes.string
}

export default Index
